import { create } from "@bufbuild/protobuf";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useCreateChecklistTemplate } from "@/hooks/useChecklistQueries";
import {
  ChecklistCategory,
  ChecklistItemSchema,
  ChecklistTemplateSchema,
  ChecklistTemplateType,
} from "@/types/proto/api/v1/checklist_service_pb";

/**
 * SaveAsTemplateDialog - Save current checklist as a template
 *
 * Features:
 * - Extract checklist from current ChecklistNode
 * - Edit title/description
 * - Choose category
 * - Save as user template
 */
interface SaveAsTemplateDialogProps {
  trigger?: React.ReactNode;
  items: Array<{
    id: string;
    text: string;
    checked: boolean;
    category?: string;
  }>;
  onSaved?: () => void;
}

export function SaveAsTemplateDialog({ trigger, items, onSaved }: SaveAsTemplateDialogProps) {
  const createTemplate = useCreateChecklistTemplate();
  const [open, setOpen] = useState(false);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<number>(ChecklistCategory.CHECKLIST_CATEGORY_UNSPECIFIED);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      toast.error("请输入模板标题");
      return;
    }

    if (items.length === 0) {
      toast.error("检查清单为空，无法保存为模板");
      return;
    }

    try {
      // Convert checklist items to proto format
      const protoItems = items.map((item) =>
        create(ChecklistItemSchema, {
          text: item.text,
          checked: false, // Reset checked state for template
          category: item.category || "",
        }),
      );

      await createTemplate.mutateAsync({
        template: create(ChecklistTemplateSchema, {
          title: title.trim(),
          description: description.trim(),
          category,
          templateType: ChecklistTemplateType.USER,
          items: protoItems,
        }),
      });

      toast.success("模板保存成功");
      setOpen(false);
      onSaved?.();

      // Reset form
      setTitle("");
      setDescription("");
      setCategory(ChecklistCategory.CHECKLIST_CATEGORY_UNSPECIFIED);
    } catch (error) {
      console.error("Failed to save template:", error);
      toast.error("保存模板失败");
    }
  };

  const itemCount = items.length;
  const checkedCount = items.filter((i) => i.checked).length;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>

      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>保存为模板</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="title">模板标题 *</Label>
            <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="例如：我的价值投资检查清单" required />
          </div>

          <div>
            <Label htmlFor="category">分类</Label>
            <Select value={category.toString()} onValueChange={(v) => setCategory(Number(v))}>
              <SelectTrigger>
                <SelectValue placeholder="选择分类" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ChecklistCategory.CHECKLIST_CATEGORY_UNSPECIFIED.toString()}>未分类</SelectItem>
                <SelectItem value={ChecklistCategory.VALUE_INVESTING.toString()}>价值投资</SelectItem>
                <SelectItem value={ChecklistCategory.TECHNICAL.toString()}>技术分析</SelectItem>
                <SelectItem value={ChecklistCategory.SPECULATION.toString()}>短线交易</SelectItem>
                <SelectItem value={ChecklistCategory.CUSTOM.toString()}>自定义</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="description">描述</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="简要描述此模板的用途..."
              rows={3}
            />
          </div>

          <div className="border rounded-lg p-3 bg-muted/30">
            <Label className="text-muted-foreground">当前检查清单</Label>
            <div className="text-sm mt-1">
              <p>
                共 {itemCount} 个检查项，已完成 {checkedCount} 个
              </p>
              <div className="mt-2 space-y-1 max-h-[120px] overflow-y-auto">
                {items.map((item) => (
                  <div key={item.id} className="flex items-center gap-2 text-xs">
                    <span
                      className={`
                        w-2 h-2 rounded-full
                        ${item.checked ? "bg-green-500" : "bg-gray-300"}
                      `}
                    />
                    <span className={item.checked ? "line-through text-muted-foreground" : ""}>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              取消
            </Button>
            <Button type="submit" disabled={createTemplate.isPending}>
              {createTemplate.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              保存模板
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
